/**
 * Support-console (Ballast staff) API + session helpers — THEME 8 Phase 2.
 *
 * The internal support console is a SEPARATE surface from the tenant app: staff
 * authenticate with a `platform:true` token against /api/support/* and are granted a
 * deliberate, audited cross-organization reach. To keep that separation clean this module
 * does NOT reuse the tenant AuthContext or its refresh machinery:
 *
 *  - the staff token lives in sessionStorage (ephemeral, cleared when the tab closes —
 *    appropriate for a privileged internal tool), under keys distinct from the tenant's,
 *  - there is NO refresh token (Phase 1 issues a 1-hour access token only), so on expiry we
 *    simply clear the session and the guard bounces to /support/login,
 *  - `supportRequest` is a dedicated fetch wrapper that never calls the tenant refresh
 *    endpoint or redirects to the tenant /login.
 *
 * @module support/staffApi
 */

import type { ApiResult } from '../api/client';

const BASE_URL = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '';

/** sessionStorage keys for the staff access token + cached staff profile. */
export const STAFF_TOKEN_KEY = 'sbp_staff_token';
export const STAFF_INFO_KEY = 'sbp_staff';

export type StaffRole = 'support' | 'superadmin';

export interface StaffInfo {
  id: string;
  email: string;
  role: StaffRole;
  firstName: string | null;
  lastName: string | null;
}

/** Returns the stored staff access token, or null if not signed in. */
export function getStaffToken(): string | null {
  return sessionStorage.getItem(STAFF_TOKEN_KEY);
}

/** Returns the cached staff profile, or null if not signed in / unparseable. */
export function getStaffInfo(): StaffInfo | null {
  const raw = sessionStorage.getItem(STAFF_INFO_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StaffInfo;
  } catch {
    return null;
  }
}

/** Persists a staff session after a successful login. */
export function setStaffSession(token: string, info: StaffInfo): void {
  sessionStorage.setItem(STAFF_TOKEN_KEY, token);
  sessionStorage.setItem(STAFF_INFO_KEY, JSON.stringify(info));
}

/** Clears the staff session (sign out / token expiry). */
export function clearStaffSession(): void {
  sessionStorage.removeItem(STAFF_TOKEN_KEY);
  sessionStorage.removeItem(STAFF_INFO_KEY);
}

/**
 * Makes a request to the support API and unwraps the { success, data, error, message }
 * envelope. Attaches the staff bearer token when present. On an auth failure
 * (expired/invalid token) it clears the local session so the guard can redirect — it does
 * NOT attempt a refresh or touch the tenant login (the two surfaces stay independent).
 */
export async function supportRequest<T>(
  path: string,
  options: RequestInit = {},
  /**
   * Explicit bearer token to use instead of the stored session token. Pass a step token
   * (mfaToken / enrollToken) during the login/enrollment flow, or `null` for a public call
   * (no Authorization header). When omitted, the stored staff session token is used.
   */
  authTokenOverride?: string | null,
): Promise<ApiResult<T>> {
  const token = authTokenOverride !== undefined ? authTokenOverride : getStaffToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      ...options,
      headers: { ...headers, ...(options.headers as Record<string, string> | undefined) },
    });

    type Envelope = { success?: boolean; data?: T; error?: string; message?: string };
    const raw = await res.text();
    let json: Envelope | null = null;
    if (raw) {
      try {
        json = JSON.parse(raw) as Envelope;
      } catch {
        json = null;
      }
    }
    if (json === null) {
      return {
        ok: false,
        error: 'SERVER_UNAVAILABLE',
        message: `The server returned an unexpected response (HTTP ${res.status}). Please try again in a moment.`,
      };
    }

    if (json.success && json.data !== undefined) {
      return { ok: true, data: json.data };
    }

    // Any auth failure on a request we sent with a token means the staff session is dead.
    if (
      token &&
      (json.error === 'TOKEN_EXPIRED' ||
        json.error === 'INVALID_TOKEN' ||
        json.error === 'MISSING_TOKEN' ||
        json.error === 'FORBIDDEN')
    ) {
      clearStaffSession();
    }

    return {
      ok: false,
      error: json.error ?? 'UNKNOWN_ERROR',
      message: json.message ?? 'An unexpected error occurred.',
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error. Please try again.';
    return { ok: false, error: 'NETWORK_ERROR', message };
  }
}

// ── Response shapes (mirror platformStaffService) ──────────────────────────────
export interface StaffSessionData {
  staff: StaffInfo;
  accessToken: string;
}

/** MFA is on → a code is still required (mfaToken sent to /support/mfa/verify). */
export interface StaffMfaChallenge {
  mfaRequired: true;
  mfaToken: string;
}

/** MFA is off → enrollment is mandatory (enrollToken authorizes /support/mfa/setup + /enable). */
export interface StaffEnrollmentRequired {
  mfaEnrollmentRequired: true;
  enrollToken: string;
}

/** The three possible outcomes of POST /api/support/login. */
export type StaffLoginData = StaffSessionData | StaffMfaChallenge | StaffEnrollmentRequired;

export interface StaffMfaSetupData {
  secret: string;
  otpauthUrl: string;
}

/** Returned by /support/mfa/enable — recovery codes plus the completed session. */
export interface StaffMfaEnableData extends StaffSessionData {
  recoveryCodes: string[];
}

export interface AccountLookupUser {
  id: string;
  email: string;
  role: string;
  isActive: boolean;
  isLocked: boolean;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
}

export interface AccountLookupOrg {
  id: string;
  name: string;
  ein: string | null;
  filingMode: string | null;
  /** ISO timestamp when the account was closed (TOS §11), or null if open. */
  closedAt: string | null;
  users: AccountLookupUser[];
}

/** Result of the close/reopen account actions. */
export interface OrgClosureData {
  orgId: string;
  name: string;
  closedAt: string | null;
}

export interface AccountsData {
  query: string;
  organizations: AccountLookupOrg[];
}

export interface UnlockData {
  userId: string;
  email: string;
  wasLocked: boolean;
}

export interface PasswordResetData {
  userId: string;
  email: string;
  /** True when the reset email was sent to the user. */
  emailSent: boolean;
  /**
   * Full reset link — present ONLY outside production when email is unconfigured
   * (local/sandbox relay). Never returned in production (review P1-6).
   */
  resetUrl?: string;
}

export interface ResetMfaData {
  userId: string;
  email: string;
  wasEnabled: boolean;
}

export interface SupportActivityItem {
  id: string;
  createdAt: string;
  action: string;
  reason: string | null;
  staffEmail: string;
  staffRole: string;
  targetUserEmail: string | null;
  targetEmployeeName: string | null;
  /** Affected STAFF member's email, when the action targeted a staff login. */
  targetStaffEmail: string | null;
  orgName: string | null;
  entityType: string | null;
  ipAddress: string | null;
}

export interface SupportActivityData {
  items: SupportActivityItem[];
  total: number;
  limit: number;
  offset: number;
}

/** A staff login row on the console's Staff tab. */
export interface StaffAccountSummary {
  id: string;
  email: string;
  role: StaffRole;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  lockedUntil: string | null;
  /** Hidden from the default roster when set; visible via "Show archived". */
  archivedAt: string | null;
  /** TRUE once they've acted in the console — such logins archive, never delete. */
  hasAuditHistory: boolean;
}

export interface StaffListData {
  staff: StaffAccountSummary[];
}

export interface StaffArchivedData {
  staffId: string;
  email: string;
  archived: boolean;
}

export interface StaffDeleteData {
  staffId: string;
  email: string;
}

/** POST /api/support/staff — the temp password appears here EXACTLY ONCE. */
export interface CreateStaffData {
  staff: { id: string; email: string; role: StaffRole; firstName: string | null; lastName: string | null };
  tempPassword: string;
}

export interface StaffPasswordResetData {
  staffId: string;
  email: string;
  tempPassword: string;
}

export interface StaffActiveData {
  staffId: string;
  email: string;
  isActive: boolean;
}

export interface OrgEmployeeSummary {
  id: string;
  firstName: string;
  lastName: string;
  workState: string | null;
  payFrequency: string | null;
  isActive: boolean;
  hasPayHistory: boolean;
}

export interface OrgEmployeesData {
  employees: OrgEmployeeSummary[];
}

export interface EmployeeRepairResult {
  employeeId: string;
  name: string;
}

export interface FixEmployeeConfigResult {
  employeeId: string;
  name: string;
  workState: string;
  payFrequency: string;
  /** True when the fix also created the org's missing state account for the new work state. */
  stateAccountSeeded: boolean;
}

export interface OrgStateAccountSummary {
  state: string;
  sutaRate: number;
  sutaAccountNumber: string | null;
  withholdingAccountNumber: string | null;
}

export interface OrgStateAccountsData {
  stateAccounts: OrgStateAccountSummary[];
}

export interface OrgPayRunSummary {
  id: string;
  periodStart: string;
  periodEnd: string;
  payDate: string;
  status: string;
  runType: string;
  totalGross: number;
  totalNet: number;
}

export interface OrgPayRunsData {
  payRuns: OrgPayRunSummary[];
}

export interface VoidPayRunResult {
  payRunId: string;
  periodStart: string;
  periodEnd: string;
  wasDisbursed: boolean;
}
